import React, { useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import downArrowSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import {Tabs, TabsProps, ButtonDropdown, Table, TableProps, Pagination} from '@amzn/geist-ui-components';
import Box from "@amzn/meridian/box";
import {SpaceBetween} from "@amzn/awsui-components-react";
import Column from "@amzn/meridian/column";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Input from "@amzn/meridian/input";
import Sheet from "@amzn/meridian/sheet";
import ExportToCSVButton from "../../../components/export_to_csv_button";

interface CreateWorkflowModalProps {
    open: boolean;
    onClose: () => void;
}

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({ open, onClose }) => {
    const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
    const [activeTabId, setActiveTabId] = useState<string>('workflowTab');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [paginatedItems, setPaginatedItems] = useState<{ firstName: string; lastName: string }[]>([{ "firstName": "John", "lastName": "Doe" },
        { firstName: "Jane", lastName: "Smith" }]);
    const [filterText, setFilterText] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<keyof { firstName: string; lastName: string }>('firstName');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [admins, setAdmins] = useState<string[]>(['Alvin Zhang', 'Allen McClendon']);

    const handleWorkflowChange = (id: string) => {
        const selected = workflowOptions.find(option => option.id === id);
        setSelectedWorkflow(selected?.text || null);
    };

    const workflowOptions = [
        { id: 'three_step', text: 'Three Step Workflow' },
        { id: 'two_step', text: 'Two Step Workflow' },
        { id: 'one_step', text: 'One Step Workflow' },
    ];

    const onSortingChange: TableProps['onSortingChange'] = (event) => {
        const sortingColumn = event.detail.sortingColumn.sortingField as keyof {
            firstName: string;
            lastName: string;
        };
        if (sortColumn === sortingColumn) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(sortingColumn);
            setSortDirection('ascending');
        }
    };

    const handleFilterChange = (event: React.SetStateAction<string>) => {
        setFilterText(event);
    };

    const tableProps: TableProps = {
        items: paginatedItems,
        columnDefinitions: [
            {
                id: 'select',
                header: 'Select',
                cell: (item: { familyName: string; givenName: string }) => (
                    <Button
                        onClick={() => handleAddUser(`${item.givenName} ${item.familyName}`)}
                        size="small"
                        type="tertiary"
                    >
                        Select
                    </Button>
                ),
            },
            {
                id: 'firstName',
                header: 'First Name',
                cell: (item: { familyName: string; givenName: string }) => item.familyName,
                sortingField: 'firstName',
            },
            {
                id: 'lastName',
                header: 'Last Name',
                cell: (item: { familyName: string; givenName: string }) => item.givenName,
                sortingField: 'lastName',
            },
        ],
        sortingColumn: { sortingField: sortColumn },
        sortingDescending: sortDirection === 'descending',
        onSortingChange: onSortingChange,
        filter: (
            <Input placeholder="Filter by any column" value={filterText} onChange={handleFilterChange} width={500} />
        ),
    };

    const workflowContent = (
        <div style={{ padding: '1rem' }}>
            <Text variant="h3">Selected Workflow: {selectedWorkflow || 'N/A'}</Text>
            <div style={{ width: '100%', maxWidth: '300px', marginTop: '1rem' }}>
                <ButtonDropdown
                    items={workflowOptions}
                    onItemClick={(event) => handleWorkflowChange(event.detail.id)}
                >
                    {selectedWorkflow || 'Select Workflow'} <Icon tokens={downArrowSmallTokens} />
                </ButtonDropdown>
            </div>
            <Text variant="secondary" style={{ marginTop: '8px' }}>
                Workflow can be selected ONCE.
            </Text>
        </div>
    );

    const tabs: TabsProps.Tab[] = [
        {
            id: 'workflowTab',
            label: 'WorkFlow',
            content: null, // Placeholder for content, controlled by state
        },
        {
            id: 'rolesTab',
            label: 'Roles',
            content: null, // Placeholder for content, controlled by state
        },
    ];

    const [currentRole, setCurrentRole] = useState<'admin'>('admin');
    const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

    const handleAddUser = (user: string) => {
        if (currentRole === 'admin') {
            setAdmins([...admins, user]);
        }
        setIsAddUserModalOpen(true);
        setIsSheetOpen(false);
    };

    const handleOpenAddUserModal = (role: 'admin') => {
        setCurrentRole(role);
        setIsSheetOpen(true);
        setIsAddUserModalOpen(true);
    };

    const onCloseAssUserModal = () =>  {
        setIsAddUserModalOpen(false);
        setIsSheetOpen(false); }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    return (
        <Modal
            title="NML Roles and Attribute"
            open={open}
            onClose={onClose}
            scrollContainer="viewport"
            closeLabel="Close"
            aria-describedby="workflow-modal-description"
            width="800px"  // Set modal width to 60% of the page
            heightt="300px"
        >
            <Tabs
                tabs={tabs}
                activeTabId={activeTabId}
                onChange={(e) => setActiveTabId(e.detail.activeTabId)}
            />
            {activeTabId === 'workflowTab' && workflowContent}
            {activeTabId === 'rolesTab' && (
                <div style={{ padding: '1rem' }}>
                    <Box height="300px" type="fill" spacingInset="10 500 10 200">
                        <Text alignment="center" type="b600">Workflow admin user</Text>
                        <Box height="200px">
                        </Box>
                        <Row height={"10px"} alignmentHorizontal={"center"}>
                            <Button onClick={() => handleOpenAddUserModal('admin')} size="small" type="tertiary">
                                Add User
                            </Button>
                        </Row>
                    </Box>
                </div>
            )}
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button type="secondary" size="small" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="primary" size="small" onClick={onClose}>
                        Done
                    </Button>
                </Row>
            </ModalFooter>
            <Modal
                title="Add User"
                open={isAddUserModalOpen}
                onClose={onCloseAssUserModal}
                scrollContainer="viewport"
                closeLabel="Close"
                aria-describedby="workflow-modal-description"
                width="800px"  // Set modal width to 60% of the page
                heightt="300px"
            >
                    <Row height={"10px"}></Row>
                    <Input placeholder="User Name (Alias)" onChange={handleSearch} width="700px" />
                    <Row height={"10px"}></Row>
                    <Input placeholder="Email" width="100%" onChange={handleSearch} width="700px" />
                    <SpaceBetween size={"m"} direction={"vertical"}></SpaceBetween>
                    <Row height={"10px"}></Row>
                    <Button type="tertiary">Search</Button>
                    <Row height={"10px"}></Row>
                    <Table {...tableProps} />
            </Modal>
        </Modal>


    );
};

export default CreateWorkflowModal;
